<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('course.general_title') }}
        </h6>

        <CreateButton
            :label="$t('course.private_learning_coaching.module.create')"
            :to="{ name: 'coursePrivateLearningCoachingModuleForm' }">
        </CreateButton>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('course.private_learning_coaching.module.list') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            @search="getModules"/>

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getModules"/>

        <EmptyState
            v-if="!modules.data?.length"
            icon="fa-layer-group"
            title="Keine Module gefunden"
            subtitle="Es wurden keine Module gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Name</th>
              <th>Kosten</th>
              <th class="text-right">Kosten pro Sitzung</th>
              <th class="text-right">Einheiten</th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">{{ $t('general.actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(module) in modules.data" :key="module.id">
              <td>{{ module?.name }}</td>
              <td class="text-right">{{ module?.cost }}</td>
              <td class="text-right">{{ module?.unitsPerMeeting }}</td>
              <td class="text-right">{{ module?.units }}</td>
              <td class="text-right">
                <button class="btn btn-primary"
                        @click="router.push({name: 'coursePrivateLearningCoachingModuleForm', params: {id: module?.id} })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ml-2"
                        @click="deleteModal(module?.id)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="modules?.pagination?.total_pages > 1"
            :totalPages="modules?.pagination?.total_pages"
            :currentPage="modules?.pagination?.current_page"
            :totalItems="modules?.pagination?.total_items"
            :perPage="modules?.pagination?.items_per_page"
            @pagechanged="getModules"/>
      </div>
    </div>

    <ModalDelete
        @update:modelValue="updateModal"
        :modelValue="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="deleteModule">
    </ModalDelete>

  </PageTemplate>
</template>


<script setup>
import coursePrivateLearningCoachingModuleService from '@/api/Course/PrivateLearningCoachingModule';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import router from '@/router';
import Search from '@/views/App/Course/PrivateLearningCoachingModule/Search.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t }       = useI18n();
const showModal   = ref(false);
const moduleId    = ref(0);
const isSearching = ref(false);
const isLoaded    = ref(false);
const modules     = ref([]);
const filters     = ref({
  name: '',
  operationType: '',
});

async function getModules(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = modules.value?.pagination?.items_per_page;

  try {
    const { data } = await coursePrivateLearningCoachingModuleService.getModules({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    modules.value     = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function deleteModule() {
  try {
    await coursePrivateLearningCoachingModuleService.deleteModule(moduleId.value);

    filters.value.name          = '';
    filters.value.operationType = '';
    showModal.value             = false;

    await getModules();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function updateModal(value) {
  CloseToast();
  showModal.value = value;
}

function deleteModal(id) {
  CloseToast();
  module.value    = id;
  showModal.value = true;
}

onMounted(async () => {
  await getModules();
  isLoaded.value = true;
});
</script>

<style scoped>

</style>